import { useState } from "react";
import { Popover } from "antd";
import { Link, useLocation } from "react-router-dom";
import { Bell, LifeBuoy, ShoppingCart, User } from "lucide-react";

import CartContent from "components/cart-content/cart-content";
import HelpFeedbackModal from "components/help-feedback-modal/help-feedback-modal";
import NotificationsList from "components/notification-list/notification-list";
import ProfileContent from "components/profile-content/profile-content";
import QuantityBadge from "components/quantity-badge/quantity-badge";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";

import { useGetNotificationCountQuery } from "graphql/queries/get-notification-count.generated";
import { useProfilePhotoUpload } from "hooks/useProfilePhotoUpload";
import { useCartStatus } from "state/reactive-vars/cart-status-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useNavProfile } from "hooks/useNavProfile";
import routeNames from "constants/route-names";
import { NavProfile } from "constants/field-values";

interface SidebarFooterProps {
	isCollapsed: boolean;
}

const SidebarFooter = ({ isCollapsed }: SidebarFooterProps) => {
	const { loggedUserValue } = useLoggedUser();
	const { cartStatusValue } = useCartStatus();
	const { navProfile } = useNavProfile();
	const uploadProfilePhoto = useProfilePhotoUpload();
	const isNotificationsPage = useLocation().pathname === routeNames.NOTIFICATIONS;

	// The Ollie profile has no commerce nav item either, so it has no cart.
	const showCart = navProfile.id !== NavProfile.Ollie;

	const [isCartOpen, setIsCartOpen] = useState(false);
	const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const [isHelpFeedbackOpen, setIsHelpFeedbackOpen] = useState(false);
	const [isUploadPhotoModalOpen, setIsUploadPhotoModalOpen] = useState(false);

	const { data: notificationCountData } = useGetNotificationCountQuery({
		skip: !loggedUserValue.id,
		variables: { receiver_id: loggedUserValue.id }
	});
	const hasNotifications = (notificationCountData?.notifications_aggregate?.aggregate?.count ?? 0) > 0;
	const loggedUserOrganizationName = loggedUserValue.organization?.name;

	return (
		<div className={`sidebar-footer ${isCollapsed ? "sidebar-footer-collapsed" : ""}`}>
			<Popover
				placement="rightBottom"
				trigger="click"
				rootClassName="notifications-popover"
				title={
					<div className="notifications-popover-title-container">
						<div>Notifications</div>
						<img
							src={`${process.env.PUBLIC_URL}/assets/images/icons/x.svg`}
							onClick={() => setIsNotificationsOpen(false)}
							style={{ cursor: "pointer", width: "15px" }}
						/>
					</div>
				}
				content={
					// The list is mounted only while the panel is open, so it loads fresh data each time.
					isNotificationsOpen && (
						<>
							<NotificationsList isHeader />
							{!isNotificationsPage && (
								<Link to={routeNames.NOTIFICATIONS} className="notifications-view-more">
									View More <img src={`${process.env.PUBLIC_URL}/assets/images/icons/external-link-red.svg`} />
								</Link>
							)}
						</>
					)
				}
				open={isNotificationsOpen}
				onOpenChange={setIsNotificationsOpen}
				fresh={true}
			>
				<button type="button" className="sidebar-footer-button" title="Notifications">
					<span className="sidebar-footer-icon">
						<Bell size={20} />
						{hasNotifications && <span className="sidebar-footer-notification-dot" />}
					</span>
					<span className="sidebar-footer-label">Notifications</span>
				</button>
			</Popover>

			{showCart && (
				<Popover
					placement="rightBottom"
					trigger="click"
					rootClassName="notifications-popover"
					title={
						<div className="notifications-popover-title-container">
							<div>My Cart</div>
							<img
								src={`${process.env.PUBLIC_URL}/assets/images/icons/x.svg`}
								onClick={() => setIsCartOpen(false)}
								style={{ cursor: "pointer" }}
							/>
						</div>
					}
					content={<CartContent />}
					open={isCartOpen}
					onOpenChange={setIsCartOpen}
				>
					<button type="button" className="sidebar-footer-button" title="Cart">
						<span className="sidebar-footer-icon">
							<ShoppingCart size={20} />
							{cartStatusValue.totalItems ? <QuantityBadge quantity={cartStatusValue.totalItems} /> : null}
						</span>
						<span className="sidebar-footer-label">Cart</span>
					</button>
				</Popover>
			)}

			<button
				type="button"
				className="sidebar-footer-button"
				title="Help & Feedback"
				onClick={() => setIsHelpFeedbackOpen(true)}
			>
				<span className="sidebar-footer-icon">
					<LifeBuoy size={20} />
				</span>
				<span className="sidebar-footer-label">Help &amp; Feedback</span>
			</button>

			<Popover
				placement="rightBottom"
				trigger="click"
				rootClassName="profile-popover"
				content={
					<ProfileContent
						openAddPhotoModal={() => setIsUploadPhotoModalOpen(true)}
						togglePopover={setIsProfileOpen}
					/>
				}
				open={isProfileOpen}
				onOpenChange={setIsProfileOpen}
			>
				<button type="button" className="sidebar-footer-profile" title="My Profile">
					<span className="sidebar-footer-avatar">
						{loggedUserValue.profile_photo ? <img src={loggedUserValue.profile_photo} alt="" /> : <User size={18} />}
					</span>
					<span className="sidebar-footer-profile-text">
						<span className="sidebar-footer-profile-name">{loggedUserValue.first_name}</span>
						{loggedUserOrganizationName && (
							<span className="sidebar-footer-profile-organization">
								<span className="sidebar-footer-profile-organization-name">{loggedUserOrganizationName}</span>
							</span>
						)}
					</span>
				</button>
			</Popover>

			<HelpFeedbackModal open={isHelpFeedbackOpen} onClose={() => setIsHelpFeedbackOpen(false)} />

			<UploadPhotoModal
				open={isUploadPhotoModalOpen}
				onCancel={() => setIsUploadPhotoModalOpen(false)}
				uploadMethod={uploadProfilePhoto}
			/>
		</div>
	);
};

export default SidebarFooter;
